/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

/**
 * Animates a count from `start` to `end` over `duration` milliseconds, calling
 * `onUpdate` with a rounded intermediate value on each frame and `end` exactly
 * when the animation completes.
 *
 * @returns A function that cancels the animation.
 */
export function animateCounter(
    start: number,
    end: number,
    duration: number,
    onUpdate: (current: number) => void
): () => void {
    const range = end - start
    let startTime: number | undefined
    let frame = requestAnimationFrame(step)

    function step(now: number) {
        // Use the first frame's timestamp as the start. It may precede the
        // `performance.now()` value from when the animation was requested.
        startTime ??= now
        const progress = Math.min((now - startTime) / duration, 1)

        if (progress < 1) {
            onUpdate(Math.round(start + range * easeOutLog(progress)))
            frame = requestAnimationFrame(step)
        } else {
            onUpdate(end)
        }
    }

    return () => cancelAnimationFrame(frame)
}

/**
 * Maps progress to a logarithmic ease-out curve. The count covers roughly
 * half its distance in the first 10% of the duration, then slows as it
 * approaches the target.
 */
function easeOutLog(t: number): number {
    const LOG_BASE = 100
    return Math.log10(1 + (LOG_BASE - 1) * t) / Math.log10(LOG_BASE)
}
